import { useQuery } from '@tanstack/react-query'
import { Navigate, useLocation } from 'react-router-dom'

import { Button } from '../../ui/Button/Button'
import { Card } from '../../ui/Card/Card'
import { Spinner } from '../../ui/Spinner/Spinner'
import { useAuth } from '../auth/AuthProvider'
import type { DashboardUser } from '../auth/auth-adapter'
import {
  getOnboardingApiClient,
  OnboardingApiError,
  type OnboardingApiClient,
} from './onboarding-api'
import { onboardingStatusQueryOptions } from './onboarding-query'
import { destinationPath, routeOnboardingResponse } from './onboarding-routing'
import styles from './OnboardingLayout.module.css'

export function OnboardingPage({
  client = getOnboardingApiClient(),
}: {
  client?: OnboardingApiClient
}) {
  const auth = useAuth()
  if (auth.status !== 'authenticated') {
    return <Spinner label="Restoring your setup" />
  }
  return <AuthenticatedOnboardingPage client={client} user={auth.user} />
}

function AuthenticatedOnboardingPage({
  client,
  user,
}: {
  client: OnboardingApiClient
  user: DashboardUser
}) {
  const auth = useAuth()
  const location = useLocation()
  const query = useQuery(onboardingStatusQueryOptions(client, user))

  if (query.isPending) return <Spinner label="Checking your setup" />
  if (query.isError) {
    if (
      query.error instanceof OnboardingApiError &&
      query.error.code === 'unauthorized'
    ) {
      return (
        <Navigate
          to="/login"
          replace
          state={{ from: location.pathname, reauthenticate: true }}
        />
      )
    }
    const classificationRequired =
      query.error instanceof OnboardingApiError &&
      query.error.code === 'classification-required'
    return (
      <Card className={styles.panel}>
        <h1>
          {classificationRequired
            ? 'Your account needs review'
            : 'Setup could not be loaded'}
        </h1>
        <p>
          {classificationRequired
            ? 'Flio support must classify this account before setup can continue.'
            : 'Please retry. If the problem continues, contact Flio support.'}
        </p>
        <div className={styles.actions}>
          <Button type="button" onClick={() => void query.refetch()}>
            Retry
          </Button>
          <Button
            type="button"
            variant="secondary"
            onClick={() => void auth.logout()}
          >
            Sign out
          </Button>
        </div>
      </Card>
    )
  }

  const destination = routeOnboardingResponse(query.data)
  if (destination !== 'onboarding') {
    return <Navigate to={destinationPath(destination)} replace />
  }

  if (
    query.data.schemaVersion !== 2 ||
    query.data.principalType !== 'CLINIC_OWNER'
  ) {
    return <Navigate to="/" replace />
  }

  return (
    <Card className={styles.panel}>
      <h1>Clinic setup</h1>
      <p>
        Your setup is ready for the next step, which is not available in this
        release.
      </p>
      <p className={styles.status}>
        Current step:{' '}
        {query.data.resumeState.replaceAll('_', ' ').toLowerCase()}
      </p>
      <Button
        type="button"
        variant="secondary"
        onClick={() => void auth.logout()}
      >
        Sign out
      </Button>
    </Card>
  )
}
